import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Mail, Phone, MapPin, User, Building2, MessageSquare } from 'lucide-react';

const PROJECT_TYPES = [
  'AI / Machine Learning',
  'Full-Stack Platform',
  'Staff Augmentation',
  'Mobile App',
  'Other',
];

const CTASection = () => {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add('visible'); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwIczBEx9UznKwqeepmEQkv3bToyDsI4CBszTWbp4Q7JN0GB1r3jtrmhlKq3-tdojZfBg/exec';

    // Hidden iframe form submission — bypasses CORS completely
    let iframe = document.getElementById('gsheet-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'gsheet-iframe';
      iframe.name = 'gsheet-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = GOOGLE_SHEETS_URL;
    form.target = 'gsheet-iframe';

    Object.entries(formData).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', projectType: '', message: '' });
    }, 1000);
  };

  const update = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="reveal relative py-24 sm:py-32 overflow-hidden"
      style={{ background: '#F8FAFC' }}
    >
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2"
          style={{ width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(38,147,255,0.05) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-0"
          style={{ width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(38,147,255,0.04) 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-14">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 font-heading" style={{ color: '#2693FF' }}>
            Get Started
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 font-heading mb-5 leading-tight">
            Let's build your next product together
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Tell us about your project. We'll respond within 24 hours with a tailored
            plan — including team composition, timeline, and fixed-fee estimate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Left: Contact info + trust signals */}
          <div className="lg:col-span-2 flex flex-col justify-center">
            {/* Contact details */}
            <div className="space-y-5 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(38,147,255,0.08)' }}>
                  <Mail className="w-5 h-5" style={{ color: '#2693FF' }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Email us</div>
                  <a href="mailto:hello@cliktechnologies.com" className="text-sm text-slate-500 hover:text-[#2693FF] transition-colors">
                    hello@cliktechnologies.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(38,147,255,0.08)' }}>
                  <Phone className="w-5 h-5" style={{ color: '#2693FF' }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Call us</div>
                  <a href="tel:+16473896552" className="text-sm text-slate-500 hover:text-[#2693FF] transition-colors">
                    +1-647-389-6552
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(38,147,255,0.08)' }}>
                  <MapPin className="w-5 h-5" style={{ color: '#2693FF' }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Offices</div>
                  <div className="text-sm text-slate-500">San Francisco &middot; India</div>
                </div>
              </div>
            </div>

            {/* Trust signals */}
            <div className="border-t border-slate-200 pt-8">
              <div className="space-y-3">
                {[
                  'Typically respond within 24 hours',
                  'No commitment required — just a conversation',
                  'Access IIT and top 1% engineering talent',
                  'Fixed-fee estimates with no hidden costs',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-slate-600">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(38,147,255,0.1)' }}>
                      <Check className="w-3 h-3" style={{ color: '#2693FF' }} />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div
              className="bg-white rounded-2xl p-7 sm:p-9 border border-slate-100"
              style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.06)' }}
            >
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(38,147,255,0.1)' }}>
                    <Check className="w-8 h-8" style={{ color: '#2693FF' }} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 font-heading mb-2">
                    We'll be in touch
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Our team will reach out within 24 hours to discuss your project.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-sm font-medium cursor-pointer"
                    style={{ color: '#2693FF' }}
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name & Email row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="cta-name" className="block text-sm font-medium text-slate-700 mb-1.5">
                        Full name <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="cta-name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={update('name')}
                          placeholder="John Smith"
                          className="form-input w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="cta-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                        Work email <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="cta-email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={update('email')}
                          placeholder="john@company.com"
                          className="form-input w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company & Project Type row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="cta-company" className="block text-sm font-medium text-slate-700 mb-1.5">
                        Company
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="cta-company"
                          type="text"
                          value={formData.company}
                          onChange={update('company')}
                          placeholder="Acme Inc."
                          className="form-input w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="cta-project-type" className="block text-sm font-medium text-slate-700 mb-1.5">
                        What do you need?
                      </label>
                      <select
                        id="cta-project-type"
                        value={formData.projectType}
                        onChange={update('projectType')}
                        className="form-input w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none appearance-none bg-white cursor-pointer"
                        style={{ color: formData.projectType ? '#0f172a' : '#94a3b8' }}
                      >
                        <option value="" disabled>Select a service</option>
                        {PROJECT_TYPES.map((pt) => (
                          <option key={pt} value={pt}>{pt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="cta-message" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Tell us about your project <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                      <textarea
                        id="cta-message"
                        rows={4}
                        required
                        value={formData.message}
                        onChange={update('message')}
                        placeholder="We're looking to build..."
                        className="form-input w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm resize-none focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gradient w-full inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl text-white font-semibold text-sm cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ boxShadow: '0 4px 16px rgba(38,147,255,0.25)' }}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Get your project plan
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-slate-400 text-center">
                    No spam. Your information is secure and never shared.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
